import { useTheme } from '@emotion/react';
import { Fragment, memo, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMedia } from 'react-use';

import type { StudentAchievement } from '@didaskill/domain/users/types';
import { ACHIEVEMENT_BACKGROUND_SIZE_MAP, Achievement, Tooltip } from '@didaskill/ui/design-system';
import { achievementMessages } from '@didaskill/ui/intl';

import { EmptyStateAchievement } from '../empty-state-achievement/EmptyStateAchievement';
import messages from './AchievementsList.messages';
import * as styles from './AchievementsList.styles';

const ACHIEVEMENT_SLOT_IDS = [
  'achievement-slot-1',
  'achievement-slot-2',
  'achievement-slot-3',
  'achievement-slot-4',
  'achievement-slot-5',
  'achievement-slot-6',
] as const;

type AchievementsListProps = {
  achievements: StudentAchievement[];
};

const AchievementsListBase = ({ achievements }: AchievementsListProps) => {
  const intl = useIntl();

  const { breakpoints } = useTheme();

  const isAboveSmall = useMedia(`(min-width: ${breakpoints.small})`);

  const achievementsWithPlaceholders = useMemo(
    () => (
      <Fragment>
        {ACHIEVEMENT_SLOT_IDS.map((slotId, index) =>
          index < achievements.length ? (
            <Achievement
              key={`${achievements[index].tier}-${achievements[index].type}`}
              type={achievements[index].type}
              tier={achievements[index].tier}
              size={isAboveSmall ? Achievement.SIZES.OVERSIZE : Achievement.SIZES.MEDIUM}
              title={intl.formatMessage(
                achievementMessages[achievements[index].type][achievements[index].tier].TITLE,
              )}
            />
          ) : (
            <EmptyStateAchievement
              key={slotId}
              size={
                ACHIEVEMENT_BACKGROUND_SIZE_MAP[
                  isAboveSmall ? Achievement.SIZES.OVERSIZE : Achievement.SIZES.MEDIUM
                ]
              }
            />
          ),
        )}
      </Fragment>
    ),
    [achievements, intl, isAboveSmall],
  );

  return (
    <Tooltip
      content={<FormattedMessage {...messages.EMPTY_STATE_TOOLTIP_CONTENT} />}
      disabled={!!achievements.length}
    >
      <div css={styles.updatedAchievementsList}>{achievementsWithPlaceholders}</div>
    </Tooltip>
  );
};

AchievementsListBase.displayName = 'AchievementsList';

export const AchievementsList = memo(AchievementsListBase);
